import { useState, useEffect } from 'react';
import { formatWeekdayDayMonthYear } from '../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import EmptyState from '../../../../components/EmptyState';
import { StatusBadge } from '../../../../components/DataTable';

interface ModalContext {
  type: 'customer' | 'property' | 'room';
  id: string;
  name: string;
  propertyId?: string;
  propertyName?: string;
  properties?: { id: string }[];
}

interface BookingsModalProps {
  context: ModalContext;
  onClose: () => void;
  supabase: SupabaseClient;
}

interface Booking {
  id: string;
  status: string;
  requested_date: string;
  room_ids: string[] | null;
  requested_by_name: string | null;
  notes: string | null;
  property: { id: string; property_name: string } | null;
}

export default function BookingsModal({ context, onClose, supabase }: BookingsModalProps) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [context]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('hosp_service_requests')
        .select(`
          *,
          property:hosp_properties(id, property_name)
        `)
        .in('status', ['pending', 'confirmed', 'scheduled'])
        .order('requested_date', { ascending: true });

      if (context.type === 'customer') {
        const propertyIds = context.properties?.map(p => p.id) || [];
        if (propertyIds.length > 0) {
          query = query.in('property_id', propertyIds);
        } else {
          setBookings([]);
          setLoading(false);
          return;
        }
      } else if (context.type === 'property') {
        query = query.eq('property_id', context.id);
      } else if (context.type === 'room') {
        query = query.eq('property_id', context.propertyId).contains('room_ids', [context.id]);
      }

      const { data, error } = await query.limit(50);
      if (error) throw error;
      setBookings((data as Booking[]) || []);
    } catch (err: unknown) {
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; bg: string; color: string }> = {
      pending: { label: 'Pending', bg: 'var(--color-warning-bg)', color: 'var(--color-warning)' },
      confirmed: { label: 'Confirmed', bg: 'var(--color-info-bg)', color: 'var(--color-info-text)' },
      scheduled: { label: 'Scheduled', bg: 'var(--color-info-bg)', color: '#4338ca' }
    };
    return statusMap[status] || { label: status, bg: 'var(--color-border)', color: 'var(--color-text-light)' };
  };

  const getContextTitle = () => {
    if (context.type === 'customer') return `Bookings for ${context.name}`;
    if (context.type === 'property') return `Bookings for ${context.name}`;
    if (context.type === 'room') return `Bookings for ${context.name}`;
    return 'Bookings';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2 className="modal-title">{getContextTitle()}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <LoadingSpinner />
            </div>
          ) : bookings.length === 0 ? (
            <EmptyState message="No upcoming bookings found." />
          ) : (
            <div className="flex flex-col gap-md">
              {bookings.map((booking) => {
                const status = getStatusBadge(booking.status);
                const roomCount = booking.room_ids?.length || 0;
                return (
                  <div
                    key={booking.id}
                    style={{
                      padding: '0.75rem 1rem',
                      background: 'var(--color-bg-secondary)',
                      borderRadius: 'var(--radius-md)'
                    }}
                  >
                    <div className="flex justify-between items-center mb-xs">
                      <div className="flex items-center gap-sm">
                        <span className="fw-500">
                          {formatWeekdayDayMonthYear(booking.requested_date)}
                        </span>
                        <StatusBadge bg={status.bg} color={status.color}>{status.label}</StatusBadge>
                      </div>
                      <span className="text-xs text-muted">
                        {roomCount} room{roomCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="text-xs text-muted">
                      {booking.property?.property_name && <span>{booking.property.property_name}</span>}
                      {booking.requested_by_name && <span> • Requested by: {booking.requested_by_name}</span>}
                    </div>
                    {booking.notes && (
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '0.25rem', fontStyle: 'italic' }}>
                        {booking.notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
